import { forwardRef, type ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * The single link primitive used for every "View all", "Manage", "Go to
 * inbox", etc. link that appears at the top-right of a dashboard / page
 * section. Keeps typography, icon size, color, and hover state identical
 * everywhere so rebranding is a one-file change.
 *
 * Usage:
 *   <SectionLink to="/invoicing/invoices">View invoices</SectionLink>
 *
 * For external or non-router links pass `href` instead of `to`.
 *
 * Sizes:
 *   - 'md' (default): section header links
 *   - 'sm'          : dense row-level links (inline inside a list item)
 */
type Size = 'sm' | 'md'

type CommonProps = {
  children: ReactNode
  size?: Size
  className?: string
  /** Hide the trailing arrow. Default: false. */
  noArrow?: boolean
}

type InternalProps = CommonProps &
  Omit<LinkProps, 'to' | 'children'> & {
    to: LinkProps['to']
    href?: never
  }

type ExternalProps = CommonProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string
    to?: never
  }

export type SectionLinkProps = InternalProps | ExternalProps

const BASE =
  'inline-flex items-center gap-1 font-medium text-primary transition-colors hover:text-primary/80 hover:underline underline-offset-2'

const SIZE_CLASS: Record<Size, string> = {
  md: 'text-sm',
  sm: 'text-xs',
}

const ICON_CLASS: Record<Size, string> = {
  md: 'size-3.5',
  sm: 'size-3',
}

function Content({
  children,
  noArrow,
  size,
}: {
  children: ReactNode
  noArrow?: boolean
  size: Size
}) {
  return (
    <>
      <span>{children}</span>
      {!noArrow ? (
        <ArrowRight
          className={cn(
            ICON_CLASS[size],
            'shrink-0 transition-transform group-hover:translate-x-0.5',
          )}
          aria-hidden
        />
      ) : null}
    </>
  )
}

export const SectionLink = forwardRef<HTMLAnchorElement, SectionLinkProps>(
  function SectionLink(props, ref) {
    const { children, size = 'md', className, noArrow = false, ...rest } = props
    const classes = cn('group', BASE, SIZE_CLASS[size], className)

    if ('href' in rest && rest.href !== undefined) {
      const anchorProps = rest as React.AnchorHTMLAttributes<HTMLAnchorElement>
      return (
        <a
          ref={ref}
          className={classes}
          target={anchorProps.target ?? '_blank'}
          rel={anchorProps.rel ?? 'noopener noreferrer'}
          {...anchorProps}
        >
          <Content size={size} noArrow={noArrow}>
            {children}
          </Content>
        </a>
      )
    }

    const linkProps = rest as Omit<LinkProps, 'children'>
    return (
      <Link ref={ref} className={classes} {...linkProps}>
        <Content size={size} noArrow={noArrow}>
          {children}
        </Content>
      </Link>
    )
  },
)
